import { TFunction, useTranslation } from 'react-i18next';
import styles from './EditGameStatsPage.module.less';
import {
	Col,
	Row,
	Form,
	Button,
	Radio,
	Space,
	RadioChangeEvent,
	Checkbox,
} from 'antd';
import FormInput from 'components/form/FormItem/FormInput';
import { useHistory, useParams } from 'react-router-dom';
import api, { GameStatFormat, UnitOfMeasurement, GameStatDto } from 'api';
import { Controller, FormProvider, useForm } from 'react-hook-form';
import { projectSelector } from 'redux/selectors/projectSelector';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { useEffect, useMemo, useRef, useState } from 'react';
import OmniNotification from 'components/OmniNotification';
import { useSelector } from 'react-redux';
import { debounce } from 'lodash';
import Breadcrumb, { IRoutes } from 'components/Breadcrumb/Breadcrumb';
import FormItem from 'components/form/FormItem';

type ParamsProps = {
	gameStateId: string;
	apiName: string;
	projectId: string;
};

const defaultValues: GameStatDto = {
	apiName: '',
	title: '',
	format: GameStatFormat.Integer,
	unitOfMeasurement: UnitOfMeasurement.Inches,
	serverOnly: false,
};

const getValidationSchema = (t: TFunction) =>
	yup.object().shape({
		apiName: yup
			.string()
			.required(
				t('validation.required', {
					fieldName: t('editGameStatsPage.apiName.label'),
				}),
			)
			.max(
				1000,
				t('validation.maxLength', {
					fieldName: t('editGameStatsPage.apiName.label'),
					count: 1000,
				}),
			),
		title: yup
			.string()
			.required(
				t('validation.required', {
					fieldName: t('editGameStatsPage.title.label'),
				}),
			)
			.max(
				80,
				t('validation.maxLength', {
					fieldName: t('editGameStatsPage.title.label'),
					count: 80,
				}),
			),
		serverOnly: yup.boolean().nullable(),
	});

export default function EditGameStatsPage() {
	const { t } = useTranslation();
	const history = useHistory();
	const projectInfo = useSelector(projectSelector);
	const validationSchema: any = getValidationSchema(t);
	const projectId = Number.parseInt(useParams<ParamsProps>().projectId);
	const [routes, setRoutes] = useState<IRoutes[]>([]);
	const pageContainer = useRef<HTMLDivElement>(null);
	const [updateLoading, setUpdateLoading] = useState<boolean>(false);
	const gameStateId = Number.parseInt(useParams<ParamsProps>().gameStateId);
	const apiName = useParams<ParamsProps>().apiName;
	const pageForm = useForm<GameStatDto>({
		mode: 'onChange',
		resolver: yupResolver(validationSchema),
		defaultValues,
	});
	const watchApiName = pageForm.watch('apiName');
	const onSubmit = async (values: GameStatDto) => {
		const result = await validApiName(
			values.apiName as string,
			pageForm.formState.dirtyFields.apiName as boolean,
		);
		if (!result && pageForm.formState.dirtyFields.apiName) return;
		setUpdateLoading(true);
		try {
			await api.updateGameStat(values);
			setUpdateLoading(false);
			OmniNotification.success({
				message: t('editGameStatsPage.notification.success.message'),
				description: t('editGameStatsPage.notification.success.description', {
					title: values.title,
				}),
			});
		} catch {
			setUpdateLoading(false);
			OmniNotification.error({
				message: t('editGameStatsPage.notification.error.message'),
				description: t('editGameStatsPage.notification.error.description'),
			});
		}

		navigateToGameStatsIndex();
	};
	function navigateToGameStatsIndex() {
		history.goBack();
	}

	const validApiName = useMemo(() => {
		return debounce(
			async (apiName, isDirty: boolean) => {
				const result = await api.validApiName({
					apiName,
					projectId: projectId,
				});
				if (!result && isDirty) {
					pageForm.setError('apiName', {
						type: 'custom',
						message: t('createGameStatsPage.apiName.alreadyTaken', {
							name: apiName,
						}),
					});
					pageContainer?.current?.parentElement?.scrollTo(0, 0);
				}
				return result;
			},
			500,
			{ leading: false, trailing: true },
		);
	}, []);

	useEffect(() => {
		if (watchApiName) {
			validApiName(
				watchApiName as string,
				pageForm.formState.dirtyFields.apiName as boolean,
			);
		}
	}, [watchApiName]);

	const getGameStat = () => {
		api.getGameStat(projectInfo?.productId as string, apiName).then((res) => {
			pageForm.reset({
				title: res.title,
				apiName: res.apiName,
				format: res.format,
				unitOfMeasurement: res.unitOfMeasurement,
				id: gameStateId,
				serverOnly: res.serverOnly,
			});
		});
	};

	useEffect(() => {
		getGameStat();
	}, []);

	useEffect(() => {
		setRoutes([
			{
				path: `project/${projectInfo?.id}`,
				breadcrumbName: projectInfo?.name as string,
			},
			{
				path: 'game-services',
				breadcrumbName: t('gameServicesPage.header'),
			},
			{
				path: 'game-stats',
				breadcrumbName: t('gameStatsPage.header'),
			},
			{
				path: '',
				breadcrumbName: t('gameStatsPage.actions.edit'),
			},
		]);
	}, [projectInfo?.id, projectInfo?.name]);

	return (
		<div className={styles.pageContainer} ref={pageContainer}>
			<Breadcrumb routes={routes} />
			<div className="page-head">
				<div>
					<h2 className="h2" style={{ wordBreak: 'break-all' }}>
						{t('editGameStatsPage.header')}
					</h2>
				</div>
			</div>
			<FormProvider {...pageForm}>
				<Form
					data-testid="achievementForm"
					layout="vertical"
					onFinish={pageForm.handleSubmit(onSubmit)}
				>
					<Row gutter={16}>
						<Col span={16}>
							<Row gutter={24}>
								<Col span={12} data-testid="apiNameGroup">
									<FormInput
										control={pageForm.control}
										translationKey="editGameStatsPage.apiName"
									/>
								</Col>
								<Col span={12} data-testid="titleGroup">
									<FormInput
										control={pageForm.control}
										translationKey="editGameStatsPage.title"
									/>
								</Col>
							</Row>
						</Col>
					</Row>
					<Row gutter={24} className={styles.formRow}>
						<Col span={24}>
							<Row>{t('editGameStatsPage.format.label')}</Row>
							<Row className="ant-form-item-explain hint">
								{t('editGameStatsPage.format.hint')}
							</Row>
						</Col>
					</Row>
					<Row>
						<Col span={24} style={{ marginLeft: 20, marginTop: 4 }}>
							<Controller
								control={pageForm.control}
								name="format"
								render={({ field: { onChange, value } }) => (
									<Radio.Group onChange={onChange} value={value}>
										<Space direction="vertical">
											<Radio
												data-testid="gameStatFormatInteger"
												value={GameStatFormat.Integer}
											>
												{t(
													`editGameStatsPage.format.gameStatFormatEnum.integer`,
												)}
											</Radio>
											<Radio
												data-testid="gameStatFormatDecimal"
												value={GameStatFormat.Decimal}
											>
												{t(
													`editGameStatsPage.format.gameStatFormatEnum.decimal`,
												)}
											</Radio>
											<Radio
												data-testid="gameStatFormatPercentage"
												value={GameStatFormat.Percentage}
											>
												{t(
													`editGameStatsPage.format.gameStatFormatEnum.percentage`,
												)}
											</Radio>
											<Radio
												data-testid="gameStatFormatHourMinutesSeconds"
												value={GameStatFormat.HourMinutesSeconds}
											>
												{t(
													`editGameStatsPage.format.gameStatFormatEnum.hourMinutesSeconds`,
												)}
											</Radio>
											<Radio
												data-testid="gameStatFormatDaysHoursMinutes"
												value={GameStatFormat.DaysHoursMinutes}
											>
												{t(
													`editGameStatsPage.format.gameStatFormatEnum.daysHoursMinutes`,
												)}
											</Radio>
										</Space>
									</Radio.Group>
								)}
							/>
						</Col>
					</Row>
					<Row gutter={24} className={styles.formRow}>
						<Col span={24}>
							<Row>{t('editGameStatsPage.unitOfMeasurement.label')}</Row>
							<Row className="ant-form-item-explain hint">
								{t('editGameStatsPage.unitOfMeasurement.hint')}
							</Row>
						</Col>
					</Row>
					<Row>
						<Col span={24} style={{ marginLeft: 20, marginTop: 4 }}>
							<Controller
								control={pageForm.control}
								name="unitOfMeasurement"
								render={({ field: { onChange, value } }) => (
									<Radio.Group onChange={onChange} value={value}>
										<Space direction="vertical">
											<Radio
												data-testid="gameStatUnitOfMeasurementInches"
												value={UnitOfMeasurement.Inches}
											>
												{t(
													`editGameStatsPage.unitOfMeasurement.unitOfMeasurementEnum.inches`,
												)}
											</Radio>
											<Radio
												data-testid="gameStatUnitOfMeasurementFeet"
												value={UnitOfMeasurement.Feet}
											>
												{t(
													`editGameStatsPage.unitOfMeasurement.unitOfMeasurementEnum.feet`,
												)}
											</Radio>
											<Radio
												data-testid="gameStatUnitOfMeasurementMiles"
												value={UnitOfMeasurement.Miles}
											>
												{t(
													`editGameStatsPage.unitOfMeasurement.unitOfMeasurementEnum.miles`,
												)}
											</Radio>
											<Radio
												data-testid="gameStatUnitOfMeasurementCentimeters"
												value={UnitOfMeasurement.Centimeters}
											>
												{t(
													`editGameStatsPage.unitOfMeasurement.unitOfMeasurementEnum.centimeters`,
												)}
											</Radio>
											<Radio
												data-testid="gameStatUnitOfMeasurementMeters"
												value={UnitOfMeasurement.Meters}
											>
												{t(
													`editGameStatsPage.unitOfMeasurement.unitOfMeasurementEnum.meters`,
												)}
											</Radio>
											<Radio
												data-testid="gameStatUnitOfMeasurementKilometers"
												value={UnitOfMeasurement.Kilometers}
											>
												{t(
													`editGameStatsPage.unitOfMeasurement.unitOfMeasurementEnum.kilometers`,
												)}
											</Radio>
											<Radio
												data-testid="gameStatUnitOfMeasurementMilliseconds"
												value={UnitOfMeasurement.Milliseconds}
											>
												{t(
													`editGameStatsPage.unitOfMeasurement.unitOfMeasurementEnum.milliseconds`,
												)}
											</Radio>
											<Radio
												data-testid="gameStatUnitOfMeasurementSeconds"
												value={UnitOfMeasurement.Seconds}
											>
												{t(
													`editGameStatsPage.unitOfMeasurement.unitOfMeasurementEnum.seconds`,
												)}
											</Radio>
											<Radio
												data-testid="gameStatUnitOfMeasurementMinutes"
												value={UnitOfMeasurement.Minutes}
											>
												{t(
													`editGameStatsPage.unitOfMeasurement.unitOfMeasurementEnum.minutes`,
												)}
											</Radio>
											<Radio
												data-testid="gameStatUnitOfMeasurementHours"
												value={UnitOfMeasurement.Hours}
											>
												{t(
													`editGameStatsPage.unitOfMeasurement.unitOfMeasurementEnum.hours`,
												)}
											</Radio>
											<Radio
												data-testid="gameStatUnitOfMeasurementDays"
												value={UnitOfMeasurement.Days}
											>
												{t(
													`editGameStatsPage.unitOfMeasurement.unitOfMeasurementEnum.days`,
												)}
											</Radio>
											<Radio
												data-testid="gameStatUnitOfMeasurementNumbers"
												value={UnitOfMeasurement.Numbers}
											>
												{t(
													`editGameStatsPage.unitOfMeasurement.unitOfMeasurementEnum.numbers`,
												)}
											</Radio>
										</Space>
									</Radio.Group>
								)}
							/>
						</Col>
					</Row>
					<Row gutter={24} className={styles.formRow}>
						<Col span={24}>
							<Row>{t('createGameStatsPage.serverOnly.label')}</Row>
							<Row>
								<FormItem
									name="serverOnly"
									control={pageForm.control}
									render={({ field }) => (
										<Checkbox
											data-testid="isServerOnlyCheckbox"
											checked={field.value}
											{...field}
										>
											{t('createGameStatsPage.serverOnly.hint')}
										</Checkbox>
									)}
								/>
							</Row>
						</Col>
					</Row>
					<Row gutter={24} style={{ marginTop: 24 }}>
						<Button
							className="ant-btn color-dark"
							data-testid="cancel"
							onClick={navigateToGameStatsIndex}
							loading={updateLoading}
						>
							{t('common.cancel')}
						</Button>
						<Button
							className="ant-btn-primary"
							data-testid="submit"
							style={{ marginLeft: '8px' }}
							htmlType="submit"
							loading={updateLoading}
						>
							{t('editGameStatsPage.update')}
						</Button>
					</Row>
				</Form>
			</FormProvider>
		</div>
	);
}
